namespace advisorop.components.common {

    /// The AI assistant's chat button, bottom right on every advisor and servicer page, and the
    /// window it opens. It sits in the page layout rather than in a route, so the conversation
    /// stays on screen while the person moves around the application.
    ///
    /// The button appears only when the server says the assistant is switched on for this person;
    /// any failure to ask hides it rather than showing a button that cannot work.
    class AiChatWidgetController {

        available = false;
        open = false;
        expanded = false;
        loaded = false;
        sending = false;
        question = "";
        messages: domains.IAiChatMessage[] = [];

        // The conversation on screen; sent with each question, so one reopened from History carries on.
        sessionId: number = null;

        // The question being answered, for the Stop button. Closing the window does not stop it: the
        // answer is there when the window is opened again.
        private asking: { requestId: string; dropRequest: ng.IDeferred<any>; stopped: boolean } = null;

        // The History list, in place of the messages while it is shown. null while it loads.
        showingHistory = false;
        history: domains.IAiChatConversationSummary[] = null;
        historyError: string = null;

        readonly suggestions = [
            "What can you help me with?",
            "How do I find a household?",
            "Who am I signed in as?"
        ];

        private static readonly OpenKey = "advisorop.aiChat.open";
        private static readonly ExpandedKey = "advisorop.aiChat.expanded";
        private static readonly SessionKey = "advisorop.aiChat.session";

        // As the server records it in place of the answer (AiChatService.StoppedAnswer).
        private static readonly StoppedAnswer = "Stopped - this question was not answered.";

        static $inject = ["aiChatService", "$q", "$timeout", "$element", "$window", "$rootRouter", "identityService"];
        constructor(private aiChatService: services.AiChatService,
            private $q: ng.IQService,
            private $timeout: ng.ITimeoutService,
            private $element: ng.IAugmentedJQuery,
            private $window: ng.IWindowService,
            private $rootRouter: any,
            private identityService: services.IdentityService) {
        }

        // The impersonation buttons under an answer. The assistant only proposes; the click is the
        // confirmation, and it goes through the same calls as the Impersonate button and the user
        // menu's "End impersonation" - including their permission check and audit - then reloads.
        confirmAction(action: domains.IAiChatAction): void {
            if ((action as any).busy) {
                return;
            }
            (action as any).busy = true;

            const call = action.type === "impersonate"
                ? this.identityService.impersonateUser(action.username)
                : this.identityService.endImpersonation();

            call.then(() => {
                // As the existing buttons do. sessionStorage is kept, so the chat window reopens.
                localStorage.clear();
                this.$window.location.href = action.url || "/";
            }, err => {
                (action as any).busy = false;
                const detail = err && typeof err.data === "string" && err.data ? err.data : "The switch did not work.";
                this.messages.push(this.prepare({ role: "assistant", content: detail, isError: true, createdOn: new Date() }));
                this.scrollToEnd();
            });
        }

        // Picking one of the numbered options sends its number, as typing it would; the server
        // turns the number into the option itself (AiChatChoices).
        pick(choice: domains.IAiChatChoice, message: domains.IAiChatMessage): void {
            if (this.sending || message.stale) {
                return;
            }
            this.question = String(choice.number);
            this.send();
        }

        isConfirmAction(action: domains.IAiChatAction): boolean {
            return action.type === "impersonate" || action.type === "endImpersonation";
        }

        $onInit(): void {
            this.expanded = this.readFlag(AiChatWidgetController.ExpandedKey);
            this.aiChatService.getStatus().then(response => {
                this.available = !!(response.data && response.data.enabled);
                if (this.available && this.readOpenState()) {
                    this.toggle();
                }
            }, () => {
                this.available = false;
            });
        }

        toggle(): void {
            this.open = !this.open;
            this.writeOpenState(this.open);

            if (this.open && !this.loaded) {
                this.loadConversation();
            }
            if (this.open) {
                this.scrollToEnd(true);
            }
        }

        // A larger window for long answers and tables. Remembered like the open state.
        toggleExpanded(): void {
            this.expanded = !this.expanded;
            this.writeFlag(AiChatWidgetController.ExpandedKey, this.expanded);
            this.scrollToEnd(true);
        }

        newConversation(): void {
            if (this.sending) {
                return;
            }
            this.aiChatService.newConversation().then(response => {
                this.messages = [];
                this.question = "";
                this.showingHistory = false;
                this.useSession(response.data && response.data.sessionId);
                this.focusInput();
            });
        }

        // Earlier conversations, latest first, each named by its first question.
        toggleHistory(): void {
            this.showingHistory = !this.showingHistory;
            if (!this.showingHistory) {
                this.scrollToEnd(true);
                return;
            }

            this.history = null;
            this.historyError = null;
            this.aiChatService.listConversations().then(response => {
                this.history = response.data || [];
            }, () => {
                this.history = [];
                this.historyError = "The conversations could not be loaded. Please try again in a moment.";
            });
        }

        // Reopens a conversation from History; the next question carries on in it.
        openConversation(conversation: domains.IAiChatConversationSummary): void {
            if (this.sending) {
                return;
            }
            if (conversation.sessionId === this.sessionId) {
                this.showingHistory = false;
                this.scrollToEnd(true);
                return;
            }

            this.historyError = null;
            this.aiChatService.getConversation(conversation.sessionId).then(response => {
                this.showConversation(response.data);
                this.showingHistory = false;
            }, () => {
                this.historyError = "That conversation could not be opened. Please try again in a moment.";
            });
        }

        askSuggestion(text: string): void {
            this.question = text;
            this.send();
        }

        onKeydown(event: KeyboardEvent): void {
            // Enter sends; Shift+Enter starts a new line.
            if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                this.send();
            }
        }

        send(): void {
            const text = (this.question || "").trim();
            if (!text || this.sending) {
                return;
            }

            // Options offered earlier refer to the previous answer only.
            this.messages.forEach(m => m.stale = true);
            this.messages.push(this.prepare({ role: "user", content: text, isError: false, createdOn: new Date() }));
            this.question = "";
            this.sending = true;
            this.showingHistory = false;
            this.scrollToEnd();

            const asking = this.asking = {
                requestId: Date.now().toString(36) + Math.random().toString(36).substring(2, 10),
                dropRequest: this.$q.defer<any>(),
                stopped: false
            };

            this.aiChatService.ask(text, this.$window.location.pathname, this.sessionId, asking.requestId, asking.dropRequest.promise).then(response => {
                const reply = response.data;
                this.useSession(reply.sessionId);
                const message = this.prepare(reply.message);
                message.toolsUsed = reply.toolsUsed || [];
                message.actions = reply.actions || [];
                this.messages.push(message);
                this.runActions(message.actions);
            }, err => {
                const detail = asking.stopped
                    ? AiChatWidgetController.StoppedAnswer
                    : err && err.status === 400 && typeof err.data === "string"
                        ? err.data
                        : "Sorry - the assistant could not answer just now. Please try again in a moment.";
                this.messages.push(this.prepare({ role: "assistant", content: detail, isError: true, createdOn: new Date() }));
            }).finally(() => {
                this.asking = null;
                this.sending = false;
                this.scrollToEnd();
                this.focusInput();
            });
        }

        // The Stop button: the window stops waiting at once, and the server stops working on the
        // question and records it as stopped.
        stop(): void {
            const asking = this.asking;
            if (!asking || asking.stopped) {
                return;
            }
            asking.stopped = true;
            asking.dropRequest.resolve();
            this.aiChatService.stop(asking.requestId).catch(() => {
                // The window has stopped waiting already; the server stops anyway when it notices the dropped request.
            });
        }

        // Opens the screen the assistant was asked for. Within the application the page is on, it
        // goes through the router exactly as a menu click does, so the window stays open. Another
        // application (advisor <-> servicer) or a page outside it (the RMD Tracker) needs a full
        // page load; the window reopens by itself afterwards (see readOpenState).
        private runActions(actions: domains.IAiChatAction[]): void {
            const navigate = (actions || []).filter(a => a.type === "navigate").pop();
            if (!navigate) {
                return;
            }

            const currentApp = (this.$window.location.pathname.split("/")[1] || "").toLowerCase();
            const targetApp = navigate.route && navigate.route.length ? String(navigate.route[0]).toLowerCase() : null;

            if (targetApp && targetApp === currentApp && this.$rootRouter) {
                this.$rootRouter.navigate(navigate.route).catch(() => {
                    this.$window.location.href = navigate.url;
                });
            } else {
                this.$window.location.href = navigate.url;
            }
        }

        // After a page load: the conversation this tab had on screen, or the sign-in's latest when the
        // server does not recognise it (another login, another impersonation).
        private loadConversation(): void {
            this.aiChatService.getConversation(this.readSession()).then(response => {
                this.showConversation(response.data);
                this.loaded = true;
            }, () => {
                this.loaded = true;
            });
        }

        private showConversation(conversation: domains.IAiChatConversation): void {
            this.messages = ((conversation && conversation.messages) || []).map(m => this.prepare(m));
            this.useSession(conversation && conversation.sessionId);
            this.scrollToEnd(true);
        }

        // The conversation on screen, remembered for this tab so that a full page load reopens it.
        private useSession(sessionId: number): void {
            this.sessionId = sessionId || null;
            try {
                if (this.sessionId) {
                    this.$window.sessionStorage.setItem(AiChatWidgetController.SessionKey, String(this.sessionId));
                } else {
                    this.$window.sessionStorage.removeItem(AiChatWidgetController.SessionKey);
                }
            } catch (e) {
                // Not remembered: a page load shows the sign-in's latest conversation instead.
            }
        }

        private readSession(): number {
            try {
                const id = parseInt(this.$window.sessionStorage.getItem(AiChatWidgetController.SessionKey), 10);
                return id > 0 ? id : undefined;
            } catch (e) {
                return undefined;
            }
        }

        private prepare(message: domains.IAiChatMessage): domains.IAiChatMessage {
            message.html = message.role === "assistant"
                ? AiChatMarkdown.toHtml(message.content)
                : AiChatMarkdown.escape(message.content).replace(/\n/g, "<br>");
            return message;
        }

        private scrollToEnd(focus = false): void {
            this.$timeout(() => {
                const list = this.$element[0].querySelector(".ai-chat-messages") as HTMLElement;
                if (list) {
                    list.scrollTop = list.scrollHeight;
                }
                if (focus) {
                    this.focusInput();
                }
            }, 0);
        }

        private focusInput(): void {
            this.$timeout(() => {
                const input = this.$element[0].querySelector(".ai-chat-input") as HTMLTextAreaElement;
                if (input) {
                    input.focus();
                }
            }, 0);
        }

        // Whether the window was open, so it stays open across a full page load. Browser storage
        // can be unavailable (private windows, blocked site data): then it simply starts closed.
        private readOpenState(): boolean {
            return this.readFlag(AiChatWidgetController.OpenKey);
        }

        private writeOpenState(open: boolean): void {
            this.writeFlag(AiChatWidgetController.OpenKey, open);
        }

        private readFlag(key: string): boolean {
            try {
                return this.$window.sessionStorage.getItem(key) === "1";
            } catch (e) {
                return false;
            }
        }

        private writeFlag(key: string, on: boolean): void {
            try {
                this.$window.sessionStorage.setItem(key, on ? "1" : "0");
            } catch (e) {
                // Not remembered; nothing else depends on it.
            }
        }
    }

    /// Just enough Markdown for chat answers: paragraphs, headings, bullet and numbered lists,
    /// tables, code, bold, italics and links. Everything is escaped first, and the result still
    /// goes through ng-bind-html's sanitizer, so nothing the model writes can become markup.
    export class AiChatMarkdown {

        static escape(text: string): string {
            return (text || "")
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;");
        }

        static toHtml(markdown: string): string {
            const lines = AiChatMarkdown.escape(markdown).replace(/\r\n/g, "\n").split("\n");
            const html: string[] = [];
            let i = 0;

            while (i < lines.length) {
                const line = lines[i];

                // Fenced code block.
                if (/^```/.test(line.trim())) {
                    const code: string[] = [];
                    i++;
                    while (i < lines.length && !/^```/.test(lines[i].trim())) {
                        code.push(lines[i]);
                        i++;
                    }
                    i++;
                    html.push(`<pre><code>${code.join("\n")}</code></pre>`);
                    continue;
                }

                // Table: a header row, a separator row of dashes, then body rows.
                if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\s*\|?[\s:\-|]+\|?\s*$/.test(lines[i + 1]) && lines[i + 1].indexOf("-") >= 0) {
                    const header = AiChatMarkdown.cells(line);
                    i += 2;
                    const rows: string[][] = [];
                    while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
                        rows.push(AiChatMarkdown.cells(lines[i]));
                        i++;
                    }
                    html.push("<table class=\"table table-condensed\"><thead><tr>"
                        + header.map(c => `<th>${AiChatMarkdown.inline(c)}</th>`).join("")
                        + "</tr></thead><tbody>"
                        + rows.map(r => "<tr>" + r.map(c => `<td>${AiChatMarkdown.inline(c)}</td>`).join("") + "</tr>").join("")
                        + "</tbody></table>");
                    continue;
                }

                // Heading.
                const heading = /^(#{1,6})\s+(.*)$/.exec(line);
                if (heading) {
                    html.push(`<h5>${AiChatMarkdown.inline(heading[2])}</h5>`);
                    i++;
                    continue;
                }

                // Bullet or numbered list.
                if (/^\s*([-*+]|\d+[.)])\s+/.test(line)) {
                    const ordered = /^\s*\d+[.)]\s+/.test(line);
                    const items: string[] = [];
                    while (i < lines.length && /^\s*([-*+]|\d+[.)])\s+/.test(lines[i])) {
                        items.push(lines[i].replace(/^\s*([-*+]|\d+[.)])\s+/, ""));
                        i++;
                        // A wrapped item continues on indented lines.
                        while (i < lines.length && /^\s{2,}\S/.test(lines[i]) && !/^\s*([-*+]|\d+[.)])\s+/.test(lines[i])) {
                            items[items.length - 1] += " " + lines[i].trim();
                            i++;
                        }
                    }
                    const tag = ordered ? "ol" : "ul";
                    html.push(`<${tag}>` + items.map(item => `<li>${AiChatMarkdown.inline(item)}</li>`).join("") + `</${tag}>`);
                    continue;
                }

                if (line.trim() === "") {
                    i++;
                    continue;
                }

                // Paragraph: consecutive plain lines.
                const paragraph: string[] = [];
                while (i < lines.length && lines[i].trim() !== "" && !/^```/.test(lines[i].trim())
                    && !/^(#{1,6})\s+/.test(lines[i]) && !/^\s*([-*+]|\d+[.)])\s+/.test(lines[i])
                    && !/^\s*\|.*\|\s*$/.test(lines[i])) {
                    paragraph.push(AiChatMarkdown.inline(lines[i]));
                    i++;
                }
                html.push(`<p>${paragraph.join("<br>")}</p>`);
            }

            return html.join("");
        }

        private static cells(row: string): string[] {
            return row.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map(c => c.trim());
        }

        // Runs on text that is already escaped.
        private static inline(text: string): string {
            return text
                .replace(/`([^`]+)`/g, "<code>$1</code>")
                .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
                .replace(/(^|[^*])\*([^*\s][^*]*)\*/g, "$1<em>$2</em>")
                // Only AdvisorOp's own (relative) addresses become links, also in the <address> form
                // Markdown allows; anything else stays text - the assistant has no reason to send
                // the user off-site, and a made-up domain must not become a working link.
                .replace(/\[([^\]]+)\]\((?:&lt;)?(\/[^\s)]*?)(?:&gt;)?\)/g, (match, label, href) => `<a href="${href}">${label}</a>`);
        }
    }

    class AiChatWidget implements ng.IComponentOptions {
        public bindings: any;
        public controller: any;
        public controllerAs: string;
        public templateUrl: string;

        constructor() {
            this.bindings = {};
            this.controller = AiChatWidgetController;
            this.controllerAs = "vm";
            this.templateUrl = 'spa/shared/common/ai-chat-widget.html';
        }
    }
    angular.module(Constants.APP).component("aiChatWidget", new AiChatWidget());
}
